import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSite } from '@/context/SiteContext';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

export const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/stay', label: 'Stay' },
  { to: '/dining', label: 'Dining' },
  {
    to: '/experience',
    label: 'Experience',
    children: [
      { to: '/experience', label: 'Island experiences', hint: 'Snorkelling, mangroves, Devil\'s Tear and more' },
      { to: '/getting-here', label: 'Getting here & boat schedule', hint: 'Airport to Ancak hill, fast boat timetable' },
    ],
  },
  { to: '/offers', label: 'Offers' },
  { to: '/journal', label: 'Journal' },
  { to: '/gallery', label: 'Gallery' },
];

export const Navbar = () => {
  const { site } = useSite();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const transparentPage = pathname === '/' || ['/stay', '/dining', '/experience'].includes(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const solid = scrolled || !transparentPage || open;
  const reserveUrl = site?.settings?.reserve_url || '#';
  const [menuOpen, setMenuOpen] = useState(null); // which dropdown is open (hover / focus / keyboard)
  useEffect(() => { setMenuOpen(null); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        'fixed top-0 inset-x-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-500',
        solid ? 'bg-background/92 backdrop-blur-md border-b border-border' : 'bg-transparent border-b border-transparent'
      )}
    >
      <div className="container-x flex items-center justify-between h-[72px] sm:h-20">
        <Link to="/" className="flex items-center" data-testid="nav-logo-link" aria-label="Bukit Ancak Lembongan Resort home">
          <img
            src={solid ? '/logos/bukit-ancak.png' : '/logos/bukit-ancak-white.png'}
            alt="Bukit Ancak Lembongan Resort"
            className="h-11 sm:h-12 w-auto transition-opacity duration-500"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9" aria-label="Primary">
          {NAV_LINKS.map((l) => {
            const linkClass = (isActive) => cn(
              'eyebrow link-underline transition-colors duration-300',
              solid ? 'text-foreground/75 hover:text-foreground' : 'text-white/85 hover:text-white',
              isActive && (solid ? 'text-foreground' : 'text-white')
            );
            if (!l.children) {
              return (
                <NavLink key={l.to} to={l.to} end={l.to === '/'} data-testid={`nav-link-${l.label.toLowerCase()}`} className={({ isActive }) => linkClass(isActive)}>
                  {l.label}
                </NavLink>
              );
            }
            const isOpen = menuOpen === l.to;
            const childActive = l.children.some((c) => pathname.startsWith(c.to));
            return (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={() => setMenuOpen(l.to)}
                onMouseLeave={() => setMenuOpen(null)}
                onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setMenuOpen(null); }}
                onKeyDown={(e) => { if (e.key === 'Escape') setMenuOpen(null); }}
                data-testid={`nav-dropdown-${l.label.toLowerCase()}`}
              >
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  aria-haspopup="menu"
                  aria-expanded={isOpen}
                  onFocus={() => setMenuOpen(l.to)}
                  className={({ isActive }) => cn(linkClass(isActive || childActive), 'inline-flex items-center gap-1')}
                >
                  {l.label}
                  <ChevronDown className={cn('h-3 w-3 transition-transform duration-200', isOpen && 'rotate-180')} aria-hidden="true" />
                </NavLink>
                {/* invisible bridge keeps the menu open while the pointer travels down */}
                <div className={cn('absolute left-1/2 -translate-x-1/2 top-full pt-4 w-80 transition-[opacity,transform] duration-200 origin-top', isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none')} role="menu" aria-label={`${l.label} menu`} data-testid={`nav-dropdown-menu-${l.label.toLowerCase()}`}>
                  <div className="rounded-xl border border-border bg-background/95 backdrop-blur-md shadow-[var(--shadow-float)] p-2">
                    {l.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        role="menuitem"
                        tabIndex={isOpen ? 0 : -1}
                        data-testid={`nav-link-${c.label.toLowerCase().replace(/[^a-z]+/g, '-').replace(/-+$/, '')}`}
                        className={({ isActive }) => cn(
                          'group/item relative block rounded-lg pl-5 pr-3.5 py-3 outline-none transition-colors duration-150',
                          // pointer / keyboard focus: navy panel with white text so the highlighted choice is unmistakable
                          'hover:bg-[hsl(var(--brand-navy))] hover:text-white focus-visible:bg-[hsl(var(--brand-navy))] focus-visible:text-white',
                          // current page: teal accent bar + teal label
                          isActive ? 'bg-secondary text-[hsl(var(--brand-teal))] before:absolute before:left-2 before:top-3 before:bottom-3 before:w-0.5 before:rounded-full before:bg-[hsl(var(--brand-teal))]' : 'text-foreground'
                        )}
                        aria-current={undefined}
                      >
                        {({ isActive }) => (
                          <>
                            <span className={cn('flex items-center justify-between gap-2 text-sm font-medium', isActive && 'group-hover/item:text-white group-focus-visible/item:text-white')}>
                              {c.label}
                              {isActive && <span className="shrink-0 whitespace-nowrap text-[10px] uppercase tracking-wider opacity-80" data-testid="nav-dropdown-current">You're here</span>}
                            </span>
                            {c.hint && <span className={cn('block text-xs mt-0.5 transition-colors', isActive ? 'text-[hsl(var(--brand-teal))]/80' : 'text-muted-foreground', 'group-hover/item:text-white/75 group-focus-visible/item:text-white/75')}>{c.hint}</span>}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={reserveUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-reserve-button"
            className="btn-gold h-10 px-4 sm:px-5"
          >
            Reserve
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className={cn('lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors', solid ? 'text-foreground hover:bg-secondary' : 'text-white hover:bg-white/10')}
                aria-label="Open menu"
                data-testid="nav-mobile-menu-button"
              >
                {<Menu className="h-5 w-5" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86vw] max-w-sm bg-background p-0" data-testid="nav-mobile-menu">
              <div className="flex flex-col h-full">
                <div className="px-7 pt-8 pb-6 border-b border-border">
                  <img src="/logos/bukit-ancak.png" alt="Bukit Ancak" className="h-12 w-auto" />
                </div>
                <nav className="flex flex-col px-7 py-6 gap-1" aria-label="Mobile">
                  {NAV_LINKS.map((l, i) => (
                    <React.Fragment key={l.to}>
                      <NavLink
                        to={l.to}
                        end={l.to === '/'}
                        data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                        className={({ isActive }) => cn('font-display text-[1.75rem] py-2.5 transition-colors', isActive ? 'text-foreground' : 'text-foreground/60 hover:text-foreground')}
                        style={{ transitionDelay: `${i * 30}ms` }}
                      >
                        {l.label}
                      </NavLink>
                      {l.children?.filter((c) => c.to !== l.to).map((c) => (
                        <NavLink key={c.to} to={c.to} data-testid={`mobile-nav-link-${c.label.toLowerCase().replace(/[^a-z]+/g, '-').replace(/-+$/, '')}`} className={({ isActive }) => cn('pl-5 -mt-1 pb-2 text-sm transition-colors', isActive ? 'text-foreground' : 'text-foreground/60 hover:text-foreground')}>
                          {c.label}
                        </NavLink>
                      ))}
                    </React.Fragment>
                  ))}
                  <Link to="/contact" className="eyebrow text-foreground/60 hover:text-foreground mt-4" data-testid="mobile-nav-link-contact">
                    Contact us
                  </Link>
                </nav>
                <div className="mt-auto px-7 pb-8">
                  <a href={reserveUrl} target="_blank" rel="noopener noreferrer" className="btn-gold w-full" data-testid="mobile-nav-reserve-button">
                    Reserve your stay
                  </a>
                  {site?.settings?.phone && <p className="mt-5 text-sm text-muted-foreground">{site.settings.phone}</p>}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
